/** Wrap an index around a list so previous on the first item lands on the last. */
export function wrapIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return ((index % length) + length) % length;
}

/**
 * Previous and next indexes around `index`, wrapping at both ends.
 * `null` when wrapping would be a no-op (fewer than two items).
 */
export function neighborIndexes(
  index: number,
  length: number
): { prev: number; next: number } | null {
  if (length < 2) return null;
  return {
    prev: wrapIndex(index - 1, length),
    next: wrapIndex(index + 1, length),
  };
}

/**
 * After removing the item at `removedIndex`, land on the item that slid into
 * that slot, or the new last item when the removed one was at the end.
 */
export function indexAfterRemove(
  removedIndex: number,
  newLength: number
): number {
  if (newLength <= 0) return 0;
  return Math.min(removedIndex, newLength - 1);
}

/**
 * Captures whose AI suggestions should be loaded when `index` is in view:
 * the current item plus both wrapped neighbours. Neighbours that collapse to
 * the same id (a two-item list) are included once.
 */
export function idsToPrefetch<T extends { id: string }>(
  queue: T[],
  index: number
): T[] {
  if (queue.length === 0) return [];
  const current = queue[wrapIndex(index, queue.length)];
  const neighbors = neighborIndexes(index, queue.length);
  if (!neighbors) return [current];
  const seen = new Set<string>();
  const out: T[] = [];
  for (const cap of [current, queue[neighbors.prev], queue[neighbors.next]]) {
    if (!seen.has(cap.id)) {
      seen.add(cap.id);
      out.push(cap);
    }
  }
  return out;
}
